import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import ModalTemplate from "../../../../../components/modal/modalTemplate";
import { fetchDocuments, uploadDocuments } from "../../../../approvalProcess/service";
import styles from "../../../../approvalProcess/components/styles";
import DivSpinner from "../../../../../components/divSpinner";
import { commonSwal } from "../../../../../components/swalAlert/swalAlert";
import { convertTimestampToFormat, formatTimestampInMap } from "../../../../../utils";
import { deleteDocument } from "../../../service";
import ActionBtnList from "../components/ActionBtnList";
import CheckLists from "../../CheckLists";
import FilesView from "../../../../approvalProcess/components/filesView/FilesView";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

const LoadingContent = () => (
    <div style={{ height: `${window.innerHeight - 387}px` }}>
        <DivSpinner />
    </div>
);

const FileStatus = ({ file }) => {
    const statusStyles = { display: "flex", alignItems: "center", gap: "4px" };

    if (file.type === "EXISTING") {
        return (
            <div style={statusStyles}>
                <i className="material-icons text-success">verified</i>
                <span className="text-sm">UPLOADED</span>
            </div>
        );
    }

    switch (file.status) {
        case "uploading":
            return (
                <div style={statusStyles}>
                    <i className="material-icons text-warning">upload</i>
                    <span className="text-sm">UPLOADING...</span>
                </div>
            );
        case "success":
            return (
                <div style={statusStyles}>
                    <i className="material-icons text-success">verified</i>
                    <span className="text-sm">UPLOADED</span>
                </div>
            );
        case "error":
            return (
                <div style={statusStyles}>
                    <i className="material-icons text-danger">error</i>
                    <span className="text-sm">ERROR</span>
                </div>
            );
        default:
            return null;
    }
};

const DocumentsFiles = ({show, handleClose, ftm_id, item, handleDocumentListRefresh}) => {
    console.log("🚀 ~ DocumentsFiles ~ item:", item)
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);
    const [acceptCheckList, setAcceptCheckList] = useState(false);

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileViewIndex, setFileViewIndex] = useState(0);

    const documentIsExpired = item?.is_expired_document;
    const isGetNewDocumentFile = item?.is_get_new_document_files;

    const handleFileViewOpen = (index) => {
        setFileViewIndex(index);
        setShowFileViewModal(true);
    }

    const handleFileViewClose = () => {
        setShowFileViewModal(false);
    }

    const updateFileStatus = useCallback((id, updates) => {
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
    }, []);

    const handleRefresh = useCallback(() => {
        handleDocumentListRefresh();
        setRefreshCount((prev) => prev + 1)
    }, [handleDocumentListRefresh]);

    useEffect(() => {
        const getCertificates = async () => {
            setLoading(true);
            try {
                const response = await fetchDocuments(ftm_id, item?.documents_types_id, isGetNewDocumentFile ? "new" : "current");
                if (response?.status === "Success") {
                    const newDocs = response?.documents?.ftm_document_files || [];
                    const mappedFiles = newDocs.map((doc, i) => {
                        const fileName = doc.ftm_document_file_path.split("/").pop();
                        const isImage = IMAGE_EXTENSIONS.some((ext) =>
                            fileName.toLowerCase().endsWith(ext)
                        );

                        return {
                            id: `${Date.now()}-${i}`,
                            file: null,
                            preview: doc.ftm_document_file_path,
                            fileName,
                            isImage,
                            status: "success",
                            ftm_document_file_id: doc.ftm_document_file_id,
                            type: "EXISTING",
                            date_time: doc.ftm_document_file_uploaded_at
                        };
                    });

                    setFiles(mappedFiles);
                    setSelectedDocument({
                        ftm_document_id: response?.documents?.ftm_document_id,
                        ftm_documents_types_id: response?.documents?.ftm_documents_types_id,
                        ftm_document_expiry_date: response?.documents?.ftm_document_expiry_date,
                        ftm_document_comment: response?.documents?.ftm_document_comment,
                        ftm_document_files: response?.documents?.ftm_document_files,
                        ftm_document_is_approve: response?.documents?.ftm_document_is_approve,
                        ftm_document_is_rejected: response?.documents?.ftm_document_is_rejected,
                        ftm_document_marked_as_concern: response?.documents?.ftm_document_marked_as_concern,
                        ftm_document_document_approve: response?.documents?.document_approve,
                        check_lists: response?.documents?.check_lists,
                    });
                } else {
                    setFiles([]);
                }
            } catch (error) {
                console.error("Error fetching documents:", error);
                setFiles([]);
            } finally {
                setLoading(false);
            }
        };

        getCertificates();
    }, [ftm_id, item?.documents_types_id, isGetNewDocumentFile, refreshCount]);

    const uploadFile = useCallback(
        async (fileObj) => {
            updateFileStatus(fileObj.id, { status: "uploading" });

            try {
                const response = await uploadDocuments({
                    ftm_id,
                    ftm_document_type_id: item?.documents_types_id,
                    ftm_document_type: item?.documents_types_name,
                    document_type: item?.documents_types_type,
                    ftm_document: fileObj.file,
                });

                const updates =
                    response?.status === "Success"
                        ? {
                            status: "success",
                            ftm_document_file_id: response.ftm_document_file_id,
                            document_path: response.document_path,
                        }
                        : { status: "error" };

                updateFileStatus(fileObj.id, updates);
                // parentRefresh();
            } catch {
                updateFileStatus(fileObj.id, { status: "error" });
            }
        },
        [ftm_id, item, updateFileStatus]
    );

    const onDrop = useCallback(
        async (acceptedFiles) => {
            const newFiles = acceptedFiles.map((file, i) => {
                const id = `${Date.now()}-${i}`;
                const isImage = IMAGE_EXTENSIONS.some((ext) =>
                    file.name.toLowerCase().endsWith(ext)
                );

                const fileObj = {
                    id,
                    file,
                    preview: URL.createObjectURL(file),
                    fileName: file.name,
                    isImage,
                    status: "pending",
                    type: "NEW",
                    date_time: Date.now(),
                };

                // uploadFile(fileObj);
                return fileObj;
            });

            setFiles((prev) => [...newFiles, ...prev]); // prepend

            for (const obj of newFiles) {
                await uploadFile(obj);
                if(obj.id === newFiles[newFiles.length -1].id){
                    handleRefresh();
                }
            }
        },
        [uploadFile, handleRefresh]
    );

    const revokePreview = useCallback((id) => {
        const file = files.find((f) => f.id === id);
        if (file?.preview) URL.revokeObjectURL(file.preview);
    }, [files]);

    const removeFile = useCallback((file) => {
        const { id, status, ftm_document_file_id } = file;

        if (status === "error") {
            revokePreview(id);
            setFiles((prev) => prev.filter((f) => f.id !== id));
        } else {
            commonSwal({
                notificationType: "UPLOAD_IMAGE_DELETE",
                preConfirm: () => {
                    updateFileStatus(id, { status: "uploading" });
                    return deleteDocument({file_id: ftm_document_file_id, document_type: 1});
                },
                isConfirmed: (result) => {
                    if (result?.isConfirmed && result?.value?.status === "Success") {
                        revokePreview(id);
                        setFiles((prev) => prev.filter((f) => f.id !== id));
                    } else {
                        updateFileStatus(id, { status: "success" }); // rollback
                    }
                },
            });
        }
    }, [revokePreview, updateFileStatus]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [], "application/pdf": [] },
        multiple: true,
    });
    
    return(
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            centered
            title={`${item?.documents_types_name} files`}
            backdrop="static"
            body={
                <section>
                    {(!isGetNewDocumentFile && documentIsExpired) && (
                        <div className="alert alert-danger alert-dismissible text-white fade show" role="alert">
                            <span className="alert-text font-weight-bold">
                                The document has passed its expiry date.
                            </span>
                        </div>
                    )}

                    {(!loading && (selectedDocument?.ftm_document_comment || selectedDocument?.ftm_document_expiry_date)) && (
                        <div className="card mb-4" style={{boxShadow: "unset"}}>
                            <div className="card-body p-3">
                                {selectedDocument?.ftm_document_comment && <p className=""><b>Comment:</b> {selectedDocument?.ftm_document_comment}</p>}
                                {selectedDocument?.ftm_document_expiry_date && <p className="mb-0"><b>Expiry date:</b> {convertTimestampToFormat(selectedDocument?.ftm_document_expiry_date)}</p>}
                            </div>
                        </div>
                    )}

                    {!loading && selectedDocument?.ftm_document_document_approve && (
                        <div className="card mb-4" style={{boxShadow: "unset"}}>
                            <div className="card-body p-3">
                                <p className="mb-0">
                                    Approved by admin <b>(ID: #{selectedDocument?.ftm_document_document_approve.admin_user.admin_id}, Name: {selectedDocument?.ftm_document_document_approve.admin_user.admin_full_name})</b> at {convertTimestampToFormat(selectedDocument?.ftm_document_document_approve.document_check_check_at)}`;
                                </p>
                            </div>
                        </div>
                    )}

                    <div {...getRootProps({ style: styles.dropzone })}>
                        <input {...getInputProps()} />
                        <p className="font-weight-bold mt-3">
                            {isDragActive ? "Drop the files here..." : "Drag & drop files or click to select"}
                        </p>
                    </div>

                    {loading ? (
                        <LoadingContent />
                    ) : (
                        <>
                            {files.length ? (
                                <aside
                                    style={{
                                        ...styles.thumbsContainer,
                                        justifyContent: window.innerWidth < 770 ? "center" : "",
                                    }}
                                >
                                    {files.map((file) => (
                                        <div key={file.id} style={styles.thumb} onClick={() => handleFileViewOpen(file.id)}>
                                            {(file.status === "success" || file.status === "error") && (
                                                <button
                                                    onClick={() => removeFile(file)}
                                                    style={styles.topRightRemoveBtn}
                                                    title="Remove"
                                                >
                                                    &times;
                                                </button>
                                            )}

                                            {file.isImage ? (
                                                <img src={file.preview} alt={file.fileName} style={styles.thumbImage} />
                                            ) : (
                                                <div style={styles.pdfPreview}>
                                                    <i className="material-icons" style={{ fontSize: "48px" }}>
                                                        file_present
                                                    </i>
                                                </div>
                                            )}

                                            <p className="mb-1 font-weight-bold text-dark" style={styles.fileName}>
                                                {file?.date_time ? formatTimestampInMap(file.date_time, true) : "N/A"}
                                            </p>
                                            <p className="font-weight-bold mb-0 pb-0" style={styles.status}>
                                                <FileStatus file={file} />
                                            </p>
                                        </div>
                                    ))}

                                     <FilesView 
                                        show={showFileViewModal} 
                                        handleClose={handleFileViewClose} 
                                        title={item?.documents_types_name + " files"} 
                                        files={files} 
                                        selectedId={fileViewIndex} 
                                    />
                                </aside>
                            ) : (
                                <div style={styles.emptyState}>
                                    <i className="material-icons" style={{ fontSize: "64px", color: "#ccc" }}>
                                        folder_open
                                    </i>
                                    <p style={{ fontSize: "14px", color: "#777" }}>No files uploaded yet</p>
                                </div>
                            )}
                        </>
                    )}

                    {!loading && (
                        <CheckLists 
                            list={selectedDocument?.check_lists} 
                            type={item?.documents_types_type} 
                            isApprove={selectedDocument?.ftm_document_is_approve === 1}
                            approveDocumentDetails={selectedDocument?.ftm_document_document_approve}
                            accepted={acceptCheckList}
                            handleAccepted={setAcceptCheckList}
                            showApprove={(!isGetNewDocumentFile && documentIsExpired)}
                        />
                    )}
                </section>
            }
            footer={
                <ActionBtnList 
                    ftm_id={ftm_id} 
                    document_type={"certificate"} 
                    selectedDocument={selectedDocument} 
                    handleRefresh={handleRefresh}
                    acceptCheckList={acceptCheckList}
                    documentIsExpired={!isGetNewDocumentFile && documentIsExpired}
                    approveNewDocument={isGetNewDocumentFile}
                    closeModal={handleClose}
                />
            }
        />
    );
};

export default DocumentsFiles;